import React, {
  Fragment,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

// material-ui
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  useMediaQuery,
  CircularProgress,
  Link,
} from "@mui/material";

// third-party
import {
  ColumnDef,
  HeaderGroup,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
  SortingState,
  FilterFn,
  ColumnMeta,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

// project-import
import ScrollX from "../../../../../components/ScrollX";
import MainCard from "../../../../../components/MainCard";
import IconButton from "../../../../../components/@extended/IconButton";
import { FormattedMessage, useIntl } from "react-intl";

import {
  DebouncedInput,
  HeaderSort,
  RowSelection,
  TablePagination,
} from "../../../../../components/@extended/react-table";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { USERAXIOS } from "../../../../../api/userAxios";
import { openSnackbar } from "api/snackbar";
import { SnackbarProps } from "types/snackbar";
import DeleteAlert from "./DeleteAlert";

interface CustomColumnMeta extends ColumnMeta<any, any> {
  className?: string;
}

export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta(itemRank);
  return itemRank.passed;
};

interface NameTranslation {
  ar: string;
}

interface TrainingCenter {
  id: number;
  user_id: number;
  user_name: string;
  name_en: string;
  name_trans: NameTranslation;
  description_en: string;
  description_trans: NameTranslation;
  url: string;
  post_office: string;
  address_en: string;
  address_trans: NameTranslation;
  phone_number: string;
  nationality: string;
  commercial_register: string;
  employees_num: number;
  expected_employees_num: number | null;
  logo: string | null;
  company_type: string | null;
  contacted: boolean;
  client: any | null;
  satisfied: boolean;
  culture: any | null;
  financial_ability: any | null;
  service_required: any | null;
  our_contact: any | null;
  hr_dept: boolean;
  trusted: boolean;
  apply_on_other_site: boolean;
  apply_url: string | null;
  pdf_mode: boolean;
  email: string;
  country_id: number;
  city_id: number;
  optional_end_date: boolean;
  registration_date: string;
  employee: any | null;
  company_activities: any[];
  created_at: string;
  updated_at: string;
}

function ReactTable({
  countPage,
  setPageSize,
  pageSize,
  currentPage,
  handlePageChange,
  data,
  columns,
  searchText,
  setCurrenttPage,
  modalToggler,
  handleSearchChange,
  searchedtext,
  setsearchedtext,
  loadingSearsh,
}: any) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "id",
      desc: false,
    },
  ]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const intl = useIntl();

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter,
    debugTable: true,
  });

  const backColor = alpha(theme.palette.primary.light, 0.1);

  return (
    <MainCard content={false}>
      <Grid container direction={"column"}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            ...(matchDownSM && {
              "& .MuiOutlinedInput-root, & .MuiFormControl-root": {
                width: "100%",
              },
            }),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              variant="outlined"
              value={searchText}
              onChange={(e) => setsearchedtext(e.target.value)}
              placeholder={intl.formatMessage({
                id: "Search records..",
                defaultMessage: "Search records..",
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" onClick={handleSearchChange}>
              <FormattedMessage id="Search" />
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={modalToggler}
              >
                <FormattedMessage id="Add New" />
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <Divider />
      </Grid>

      <ScrollX>
        <Stack>
          <RowSelection selected={Object.keys(rowSelection).length} />
          <TableContainer>
            <Table>
              <TableHead>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        if (
                          header.column.columnDef.meta !== undefined &&
                          header.column.getCanSort()
                        ) {
                          Object.assign(header.column.columnDef.meta, {
                            className:
                              header.column.columnDef.meta.className +
                              " cursor-pointer prevent-select",
                          });
                        }

                        return (
                          <TableCell
                            key={header.id}
                            {...header.column.columnDef.meta}
                            onClick={header.column.getToggleSortingHandler()}
                            {...(header.column.getCanSort() &&
                              header.column.columnDef.meta === undefined && {
                                className: "cursor-pointer prevent-select",
                              })}
                          >
                            {header.isPlaceholder ? null : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Box>
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Box>
                                {header.column.getCanSort() && (
                                  <HeaderSort column={header.column} />
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          {...cell.column.columnDef.meta}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow
                        sx={{
                          bgcolor: backColor,
                          "&:hover": { bgcolor: `${backColor} !important` },
                        }}
                      >
                        <TableCell colSpan={row.getVisibleCells().length}>
                          {/* Add expanded row content if needed */}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <TablePagination
                {...{
                  setPageSize: setPageSize,
                  setPageIndex: setCurrenttPage,
                  getState: table.getState,
                  getPageCount: () => countPage.last_page,
                  currentPage: currentPage,
                  handlePageChange: handlePageChange,
                  initialPageSize: pageSize,
                }}
              />
            </Box>
          </>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

const TrainingCenters = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TrainingCenter[]>([]);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<TrainingCenter | null>(
    null
  );
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [searchedtext, setsearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { companyId } = useParams();

  const fetchData = async () => {
    setLoading(true);
    try {
      // Only fetch if companyId is a valid number
      if (companyId && !isNaN(Number(companyId))) {
        const response = await USERAXIOS.get(`/company_profiles/${companyId}`);
        const { data } = response.data;
        setResults([data]); // Set as array with single item
        setCountPage({
          current_page: 1,
          last_page: 1,
        });
      } else {
        // If no valid ID, fetch all data with search query
        const response = await USERAXIOS.get(
          `/company_profiles?page=${currentPage}${
            searchQuery ? `&title=${searchQuery}` : ""
          }`
        );
        const { data, meta } = response.data;
        setResults(data);
        setCountPage(meta);
      }
    } catch (error) {
      console.error("Error fetching training centers:", error);
      openSnackbar({
        open: true,
        message: "Error fetching training centers",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, companyId, searchQuery]);

  const columns = useMemo<ColumnDef<TrainingCenter, any>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        } as CustomColumnMeta,
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "user_name",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.user_name}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Arabic Name" />,
        accessorKey: "name_trans",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.name_trans.ar}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="City" />,
        accessorKey: "city_id",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.city_id}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="url" />,
        accessorKey: "url",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.url}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "trusted",
        cell: (cell) => {
          const status = cell.getValue();
          return (
            <Chip
              color={status ? "success" : "error"}
              label={
                status ? (
                  <FormattedMessage id="Active" />
                ) : (
                  <FormattedMessage id="Inactive" />
                )
              }
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        header: () => <FormattedMessage id="Training Courses" />,
        accessorKey: "company_id",
        cell: ({ row }) => (
          <Stack
            direction="row"
            // spacing={1.5}
            alignItems="center"
            // justifyContent="center"
          >
            <Link
              component="button"
              variant="body2"
              color="primary"
              onClick={() => {
                navigate(`/training-courses?company_id=${row.original.id}`);
              }}
              sx={{
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <EyeOutlined style={{ fontSize: "1.2rem" }} />
            </Link>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        } as CustomColumnMeta,
        disableSortBy: true,
        cell: ({ row }) => {
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    navigate(`/company-list/edit-company/${row.original.id}`);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelectedCenter(row.original);
                    setDeleteAlertOpen(true);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    []
  );

  const handleDelete = async () => {
    if (!selectedCenter) return;

    try {
      setLoading(true);
      await USERAXIOS.delete(`/company_profiles/${selectedCenter.id}`);

      const updatedResults = results.filter(
        (item) => item.id !== selectedCenter.id
      );
      setResults(updatedResults);

      openSnackbar({
        open: true,
        message: "Training center deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);
    } catch (error) {
      console.error("Error deleting training center:", error);
      openSnackbar({
        open: true,
        message: "Failed to delete training center",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
      setSelectedCenter(null);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "background.default" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ReactTable
          pageSize={pageSize}
          setPageSize={setPageSize}
          data={results}
          columns={columns}
          modalToggler={() => navigate(`/companies-list/add-company`)}
          countPage={countPage}
          currentPage={currentPage}
          setCurrenttPage={setCurrenttPage}
          handlePageChange={handlePageChange}
          searchText={searchedtext}
          handleSearchChange={handleSearch}
          searchedtext={searchedtext}
          setsearchedtext={setsearchedtext}
          loadingSearsh={loadingSearsh}
        />
      )}

      <DeleteAlert
        open={!!selectedCenter}
        onClose={() => {
          setSelectedCenter(null);
        }}
        handleDelete={handleDelete}
        title="Delete Training Center"
        message="Are you sure you want to delete this training center?"
      />
    </Box>
  );
};

export default TrainingCenters;
